import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'
import type {
	FormField,
	FormFieldCondition,
	FormFieldStaticOption,
	FormFieldType,
	FormFieldValidation,
} from '@kfolks/types'
import { FormFieldType as FormFieldTypeEnum } from '@kfolks/types'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { requestCenterApi } from '@/services/request-center-api'
import { useFormBuilderState } from './FormBuilderState'

/**
 * Right-hand properties panel for the visual form builder. Renders editors
 * for the currently-selected field's properties:
 *
 *   - id            — read-only (changing it would orphan summary-template
 *                     tokens, conditional-visibility refs, and approver-
 *                     resolver `field:<id>` strategies; rename UX is a
 *                     follow-up that surfaces a confirm + global rewrite).
 *   - label         — text input
 *   - description   — textarea (the "help text" the renderer shows under
 *                     the input). The persisted field uses `description`;
 *                     the spec calls it "help text" in admin UX.
 *   - required      — checkbox
 *   - placeholder   — text input
 *   - validation    — min / max / pattern / message inputs (only rendered
 *                     for field types where they apply)
 *   - dataSource    — select bound to the catalog of registered data
 *                     sources (only for autocomplete-style types)
 *   - options       — repeating { label, value } editor (only for
 *                     non-autocomplete selects)
 *   - conditions    — visibility rules referencing other field ids by
 *                     id with operator + optional value
 *
 * Empty-state when no field is selected (canvas not yet clicked).
 *
 * All edits flow through `updateField(id, patch)` on the shared state. The
 * spec's debounced `validateFormSchema` integration is task 7.8 — this
 * panel is the authoring surface; validation feedback layers on top.
 */
export function FieldPropertiesPanel() {
	const { formSchema, selectedFieldId, updateField, removeField, validation } = useFormBuilderState()
	const selected = useMemo(
		() => formSchema.fields.find((f) => f.id === selectedFieldId) ?? null,
		[formSchema.fields, selectedFieldId],
	)

	if (!selected) return <EmptySelectionState />
	const fieldErrors = validation.errorsByFieldId[selected.id] ?? []
	return (
		<div className="space-y-4" aria-label={`Properties for ${selected.label || selected.id}`}>
			<HeaderRow field={selected} onRemove={() => removeField(selected.id)} />
			{fieldErrors.length > 0 ? <FieldErrorsBanner errors={fieldErrors} /> : null}
			<BasicSection field={selected} onPatch={(patch) => updateField(selected.id, patch)} />
			<ValidationSection field={selected} onPatch={(patch) => updateField(selected.id, patch)} />
			<DataBindingSection field={selected} onPatch={(patch) => updateField(selected.id, patch)} />
			<ConditionsSection
				field={selected}
				allFields={formSchema.fields}
				onPatch={(patch) => updateField(selected.id, patch)}
			/>
		</div>
	)
}

function FieldErrorsBanner({ errors }: { errors: ReadonlyArray<string> }) {
	return (
		<div
			role="alert"
			data-testid="field-properties-errors"
			className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
		>
			{errors.map((msg, i) => (
				<div key={i} className="flex items-start gap-2">
					<AlertCircle className="mt-0.5 size-4 shrink-0" />
					<p className="text-xs">{msg}</p>
				</div>
			))}
		</div>
	)
}

// ─── Header ──────────────────────────────────────────────────────────────────

function HeaderRow({ field, onRemove }: { field: FormField; onRemove: () => void }) {
	return (
		<div className="flex items-start justify-between gap-2 border-b pb-3">
			<div className="min-w-0 flex-1">
				<p className="font-mono text-xs text-muted-foreground">id: {field.id}</p>
				<p className="font-mono text-xs text-muted-foreground">type: {field.type}</p>
			</div>
			<Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove field ${field.id}`}>
				<Trash2 className="size-4" />
			</Button>
		</div>
	)
}

// ─── Basic editors (label / description / required / placeholder) ───────────

function BasicSection({ field, onPatch }: { field: FormField; onPatch: (patch: Partial<FormField>) => void }) {
	return (
		<section className="space-y-3">
			<LabeledField label="Label" htmlFor="fp-label">
				<Input
					id="fp-label"
					type="text"
					value={field.label ?? ''}
					onChange={(e) => onPatch({ label: e.target.value })}
				/>
			</LabeledField>
			<LabeledField label="Help text" htmlFor="fp-description">
				<textarea
					id="fp-description"
					rows={2}
					value={field.description ?? ''}
					onChange={(e) => onPatch({ description: e.target.value || undefined })}
					className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				/>
			</LabeledField>
			<LabeledField label="Placeholder" htmlFor="fp-placeholder">
				<Input
					id="fp-placeholder"
					type="text"
					value={field.placeholder ?? ''}
					onChange={(e) => onPatch({ placeholder: e.target.value || undefined })}
				/>
			</LabeledField>
			<label className="flex items-center gap-2 text-sm">
				<input
					type="checkbox"
					checked={!!field.required}
					onChange={(e) => onPatch({ required: e.target.checked || undefined })}
					className="size-4 rounded border-input"
				/>
				Required
			</label>
		</section>
	)
}

// ─── Validation (min / max / pattern / message) ─────────────────────────────

function ValidationSection({ field, onPatch }: { field: FormField; onPatch: (patch: Partial<FormField>) => void }) {
	if (!supportsValidation(field.type)) return null
	const v: FormFieldValidation = field.validation ?? {}
	const isNumeric = field.type === FormFieldTypeEnum.NUMBER
	const setValidation = (next: Partial<FormFieldValidation>) => {
		const merged = { ...v, ...next }
		// Strip undefineds so the persisted shape is canonical (no zombie keys).
		const compact = Object.fromEntries(Object.entries(merged).filter(([, val]) => val !== undefined))
		onPatch({ validation: Object.keys(compact).length ? (compact as FormFieldValidation) : undefined })
	}

	return (
		<section className="space-y-3 border-t pt-3">
			<h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Validation</h4>
			<div className="grid grid-cols-2 gap-2">
				<LabeledField label={isNumeric ? 'Min' : 'Min length'} htmlFor="fp-min">
					<Input
						id="fp-min"
						type="number"
						value={v.min ?? ''}
						onChange={(e) => setValidation({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
					/>
				</LabeledField>
				<LabeledField label={isNumeric ? 'Max' : 'Max length'} htmlFor="fp-max">
					<Input
						id="fp-max"
						type="number"
						value={v.max ?? ''}
						onChange={(e) => setValidation({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
					/>
				</LabeledField>
			</div>
			{supportsPattern(field.type) ? (
				<LabeledField label="Pattern (regex)" htmlFor="fp-pattern">
					<Input
						id="fp-pattern"
						type="text"
						placeholder="^[A-Z]{3}$"
						value={v.pattern ?? ''}
						onChange={(e) => setValidation({ pattern: e.target.value || undefined })}
					/>
				</LabeledField>
			) : null}
			<LabeledField label="Validation message" htmlFor="fp-message">
				<Input
					id="fp-message"
					type="text"
					placeholder="Shown when validation fails"
					value={v.message ?? ''}
					onChange={(e) => setValidation({ message: e.target.value || undefined })}
				/>
			</LabeledField>
		</section>
	)
}

// ─── Data source / static options ────────────────────────────────────────────

const AUTOCOMPLETE_TYPES = new Set<FormFieldType>([
	FormFieldTypeEnum.AUTOCOMPLETE,
	FormFieldTypeEnum.AUTOCOMPLETE_MULTI,
	FormFieldTypeEnum.USER_PICKER,
	FormFieldTypeEnum.USER_PICKER_MULTI,
	FormFieldTypeEnum.PROJECT_PICKER,
	FormFieldTypeEnum.COUNTRY_PICKER,
])

const STATIC_OPTION_TYPES = new Set<FormFieldType>([
	FormFieldTypeEnum.SELECT,
	FormFieldTypeEnum.MULTISELECT,
	FormFieldTypeEnum.RADIO,
])

function DataBindingSection({ field, onPatch }: { field: FormField; onPatch: (patch: Partial<FormField>) => void }) {
	const wantsDataSource = AUTOCOMPLETE_TYPES.has(field.type)
	const wantsStaticOptions = STATIC_OPTION_TYPES.has(field.type)
	if (!wantsDataSource && !wantsStaticOptions) return null

	return (
		<section className="space-y-3 border-t pt-3">
			<h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
				{wantsDataSource ? 'Data source' : 'Options'}
			</h4>
			{wantsDataSource ? <DataSourceBinding field={field} onPatch={onPatch} /> : null}
			{wantsStaticOptions ? <StaticOptionsEditor field={field} onPatch={onPatch} /> : null}
		</section>
	)
}

function DataSourceBinding({ field, onPatch }: { field: FormField; onPatch: (patch: Partial<FormField>) => void }) {
	const sources = useQuery({
		queryKey: ['data-sources'],
		queryFn: () => requestCenterApi.listDataSources(),
	})

	return (
		<LabeledField label="Bound source" htmlFor="fp-data-source">
			<select
				id="fp-data-source"
				value={field.dataSource ?? ''}
				onChange={(e) => onPatch({ dataSource: e.target.value || undefined })}
				className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				<option value="">— None —</option>
				{(sources.data ?? []).map((s) => (
					<option key={s.sourceKey} value={s.sourceKey}>
						{s.sourceKey}
					</option>
				))}
			</select>
		</LabeledField>
	)
}

function StaticOptionsEditor({ field, onPatch }: { field: FormField; onPatch: (patch: Partial<FormField>) => void }) {
	const options: FormFieldStaticOption[] = field.options ?? []
	const update = (next: FormFieldStaticOption[]) => onPatch({ options: next.length ? next : undefined })

	return (
		<div className="space-y-2">
			{options.length === 0 ? (
				<p className="text-xs text-muted-foreground">No options yet — click "Add option" to create the first one.</p>
			) : (
				<ul className="space-y-2">
					{options.map((opt, i) => (
						// Stable key: bare index is correct here because we never reorder
						// the options list (add/edit/remove only — preserves identity at
						// each position). Including `opt.value` would force a remount on
						// every keystroke and lose focus.
						<li key={i} className="flex items-center gap-2">
							<Input
								type="text"
								placeholder="Label"
								aria-label={`Option ${i + 1} label`}
								value={opt.label}
								onChange={(e) => {
									const next = [...options]
									next[i] = { ...opt, label: e.target.value }
									update(next)
								}}
							/>
							<Input
								type="text"
								placeholder="value"
								aria-label={`Option ${i + 1} value`}
								value={opt.value}
								onChange={(e) => {
									const next = [...options]
									next[i] = { ...opt, value: e.target.value }
									update(next)
								}}
							/>
							<Button
								variant="ghost"
								size="sm"
								aria-label={`Remove option ${i + 1}`}
								onClick={() => {
									const next = options.filter((_, idx) => idx !== i)
									update(next)
								}}
							>
								<Trash2 className="size-4" />
							</Button>
						</li>
					))}
				</ul>
			)}
			<Button
				variant="outline"
				size="sm"
				onClick={() => update([...options, { label: '', value: '' }])}
				aria-label="Add option"
			>
				<Plus className="size-4" /> Add option
			</Button>
		</div>
	)
}

// ─── Conditional visibility ──────────────────────────────────────────────────

function ConditionsSection({
	field,
	allFields,
	onPatch,
}: {
	field: FormField
	allFields: ReadonlyArray<FormField>
	onPatch: (patch: Partial<FormField>) => void
}) {
	const conditions: FormFieldCondition[] = field.conditions ?? []
	const others = allFields.filter((f) => f.id !== field.id)
	const update = (next: FormFieldCondition[]) => onPatch({ conditions: next.length ? next : undefined })

	return (
		<section className="space-y-2 border-t pt-3">
			<h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Conditional visibility</h4>
			<p className="text-xs text-muted-foreground">Show this field only when every condition is satisfied (AND).</p>
			{conditions.length === 0 ? (
				<p className="text-xs text-muted-foreground">No conditions yet — this field is always shown.</p>
			) : (
				<ul className="space-y-2">
					{conditions.map((cond, i) => (
						<li key={i} className="space-y-1 rounded-md border bg-card p-2">
							<div className="grid grid-cols-2 gap-2">
								<select
									aria-label={`Condition ${i + 1} field`}
									value={cond.field}
									onChange={(e) => {
										const next = [...conditions]
										next[i] = { ...cond, field: e.target.value }
										update(next)
									}}
									className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
								>
									<option value="">— Field —</option>
									{others.map((f) => (
										<option key={f.id} value={f.id}>
											{f.id}
										</option>
									))}
								</select>
								<select
									aria-label={`Condition ${i + 1} operator`}
									value={cond.operator}
									onChange={(e) => {
										const next = [...conditions]
										next[i] = { ...cond, operator: e.target.value as FormFieldCondition['operator'] }
										update(next)
									}}
									className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
								>
									<option value="equals">equals</option>
									<option value="not_equals">not equals</option>
									<option value="contains">contains</option>
									<option value="not_empty">is not empty</option>
								</select>
							</div>
							{cond.operator !== 'not_empty' ? (
								<Input
									type="text"
									placeholder="value"
									aria-label={`Condition ${i + 1} value`}
									value={(cond.value as string | undefined) ?? ''}
									onChange={(e) => {
										const next = [...conditions]
										next[i] = { ...cond, value: e.target.value }
										update(next)
									}}
								/>
							) : null}
							<div className="flex justify-end">
								<Button
									variant="ghost"
									size="sm"
									aria-label={`Remove condition ${i + 1}`}
									onClick={() => update(conditions.filter((_, idx) => idx !== i))}
								>
									<Trash2 className="size-4" /> Remove
								</Button>
							</div>
						</li>
					))}
				</ul>
			)}
			<Button
				variant="outline"
				size="sm"
				onClick={() => update([...conditions, { field: '', operator: 'equals', value: '' }])}
				aria-label="Add condition"
			>
				<Plus className="size-4" /> Add condition
			</Button>
		</section>
	)
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptySelectionState() {
	return (
		<div
			role="region"
			aria-label="Field properties"
			className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground"
		>
			<p className="font-medium">Select a field</p>
			<p className="mt-1">Click a field on the canvas to edit its properties.</p>
		</div>
	)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function LabeledField({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
	return (
		<label htmlFor={htmlFor} className="block space-y-1 text-sm">
			<span className="font-medium text-foreground">{label}</span>
			{children}
		</label>
	)
}

/** Field types where validation min/max/pattern make sense. */
function supportsValidation(type: FormFieldType): boolean {
	return (
		type === FormFieldTypeEnum.TEXT ||
		type === FormFieldTypeEnum.TEXTAREA ||
		type === FormFieldTypeEnum.NUMBER ||
		type === FormFieldTypeEnum.EMAIL
	)
}

/** Field types where a regex pattern makes sense. */
function supportsPattern(type: FormFieldType): boolean {
	return type === FormFieldTypeEnum.TEXT || type === FormFieldTypeEnum.TEXTAREA || type === FormFieldTypeEnum.EMAIL
}
